<script setup lang="ts">
/**
 * InstallBanner.vue — prompts the user to install the PWA to their phone.
 * Mirrors UpdateBanner.vue's pattern (dismissible fixed banner, mounted once
 * in App.vue so it's reachable from every screen, including LoginView).
 *
 * There is no single "can this browser install a PWA" API, so this covers
 * two independent paths:
 *
 * - Android / desktop Chrome & Edge fire `beforeinstallprompt`. We capture
 *   that event (preventDefault() suppresses the browser's own mini-infobar)
 *   and show our own "Instalar" button that replays the stored event. If
 *   the event never fires (already installed, or an unsupported browser),
 *   this banner simply never appears — there is no fallback/no-op button.
 * - iOS Safari — and iOS Chrome, which is the same WebKit engine underneath
 *   and equally lacks this API — never fires `beforeinstallprompt` and has
 *   no programmatic install call at all. We detect iOS + "not already
 *   installed" instead and show manual instructions (Compartir -> Agregar
 *   a inicio).
 *
 * Neither banner shows if the app is already running installed
 * (display-mode: standalone, or iOS's navigator.standalone === true), nor
 * if the user dismissed/installed within the last DISMISS_TTL_DAYS days —
 * see that constant for the reasoning.
 *
 * Fixed to the BOTTOM, like UpdateBanner — NOT the top: every authenticated
 * screen (AppLayout.vue) has a `position: sticky; top: 0` topbar, and a
 * top-fixed banner with a higher z-index renders on top of it, hiding the
 * company name/home/logout controls. LoginView has no topbar, so bottom-
 * fixing costs nothing there either. When UpdateBanner is ALSO showing (rare
 * — it only appears once a new service worker is actually waiting), this
 * banner shifts up by UpdateBanner's height so the two stack instead of
 * overlapping each other at the same spot.
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { needRefresh } from '../lib/pwa-update.js';

/** Fired by Chrome/Edge (and other Chromium browsers) before they show
 * their own install UI. Not part of lib.dom.d.ts, so declared locally. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISS_KEY = 'rigx-install-dismissed';
// Re-show after a dismissal, but not every session. This is a self-service
// field app people reopen daily — nagging on every open would train them to
// ignore it, but permanently suppressing it (no TTL at all) means someone
// who dismissed on a bad day never gets asked again even if they'd install
// later. 14 days is a middle ground: long enough to not be annoying,
// short enough that a change of heart doesn't wait forever.
const DISMISS_TTL_DAYS = 14;
const DISMISS_TTL_MS = DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;

const showAndroidBanner = ref(false);
const showIosBanner = ref(false);

// Not a ref: the event object itself never needs to be reactive, only
// whether we currently hold one (showAndroidBanner covers that). It also
// can only be used once (see install()), so there's nothing to watch.
let deferredPrompt: BeforeInstallPromptEvent | null = null;

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIosSafariFamily(): boolean {
  const ua = navigator.userAgent;
  const uaLooksIos =
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ reports as "MacIntel" with touch support when requesting
    // the desktop site (its default).
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1);
  // navigator.standalone only exists on iOS's WebKit (Safari, and — since
  // it's the same engine underneath — iOS Chrome too). Its mere presence,
  // even as `false`, is itself a strong iOS signal; `false` specifically
  // means "not already running as an installed PWA".
  const nav = navigator as Navigator & { standalone?: boolean };
  return uaLooksIos && nav.standalone === false;
}

function wasRecentlyDismissed(): boolean {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const dismissedAt = Number(raw);
  if (Number.isNaN(dismissedAt)) return false;
  return Date.now() - dismissedAt < DISMISS_TTL_MS;
}

function markDismissed(): void {
  localStorage.setItem(DISMISS_KEY, String(Date.now()));
}

function onBeforeInstallPrompt(event: Event): void {
  event.preventDefault();
  if (isStandalone() || wasRecentlyDismissed()) return;
  deferredPrompt = event as BeforeInstallPromptEvent;
  showAndroidBanner.value = true;
}

async function install(): Promise<void> {
  if (!deferredPrompt) return;
  await deferredPrompt.prompt();
  await deferredPrompt.userChoice;
  // A beforeinstallprompt event can only be prompted once, win or lose.
  deferredPrompt = null;
  showAndroidBanner.value = false;
  markDismissed();
}

function dismissAndroid(): void {
  showAndroidBanner.value = false;
  deferredPrompt = null;
  markDismissed();
}

function dismissIos(): void {
  showIosBanner.value = false;
  markDismissed();
}

onMounted(() => {
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

  if (!isStandalone() && !wasRecentlyDismissed() && isIosSafariFamily()) {
    showIosBanner.value = true;
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
});

// UpdateBanner's own height (padding + line height) is stable/hardcoded in
// its CSS, not measured — matching that here is simpler and more robust
// than a ResizeObserver for a banner pair that only rarely coexists.
const UPDATE_BANNER_HEIGHT = '52px';
const stackOffset = computed(() => (needRefresh.value ? UPDATE_BANNER_HEIGHT : '0px'));
</script>

<template>
  <div
    v-if="showAndroidBanner"
    class="install-banner"
    role="status"
    :style="{ bottom: stackOffset }"
  >
    <span>Instalá la app en tu celular para acceder más rápido.</span>
    <div class="install-banner-actions">
      <button
        type="button"
        class="install-banner-button"
        @click="install"
      >
        Instalar
      </button>
      <button
        type="button"
        class="install-banner-dismiss"
        aria-label="Cerrar"
        @click="dismissAndroid"
      >
        ✕
      </button>
    </div>
  </div>
  <div
    v-else-if="showIosBanner"
    class="install-banner"
    role="status"
    :style="{ bottom: stackOffset }"
  >
    <span>Para instalar: tocá el ícono Compartir (□↑) y luego "Agregar a inicio".</span>
    <button
      type="button"
      class="install-banner-dismiss"
      aria-label="Cerrar"
      @click="dismissIos"
    >
      ✕
    </button>
  </div>
</template>

<style scoped>
.install-banner {
  position: fixed;
  left: 0;
  right: 0;
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem max(0.75rem, env(safe-area-inset-bottom));
  background: var(--panel);
  color: var(--text);
  border-top: 1px solid var(--border);
  font-size: 0.85rem;
  box-shadow: 0 -6px 20px rgba(0, 0, 0, 0.08);
}

.install-banner-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.5rem;
}

.install-banner-button {
  flex-shrink: 0;
  min-height: 44px;
  border: none;
  border-radius: var(--radius);
  padding: 0.5rem 0.9rem;
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--accent-text);
  background: var(--accent);
  cursor: pointer;
}

.install-banner-dismiss {
  flex-shrink: 0;
  min-height: 44px;
  min-width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  background: var(--panel);
  color: var(--muted);
  font-size: 1rem;
  cursor: pointer;
}
</style>
